import moment from 'moment'
import 'react-dates/initialize'
import { useState, useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import TimezoneContext from 'context/timezoneContext'
import { useRouter } from 'next/router'
import { useUser } from 'providers/UserProvider'
import { useQuery } from '@tanstack/react-query'

import Wrapper from 'components/Wrapper'

import {
  getPaymentMethodsInfo,
  getProfessionalInfoBySlug,
  getScheduleByProfessionalSlug
} from 'services/professionalService'
import { getProfessionalEvaluations } from 'services/evaluation'
import { formatAppointmentDates } from 'utils/formatters'
import {
  navHeight,
  getYouTubeVideoId,
  getDataFromScheduleResponse,
  calculateNewDates,
  getPaymentsInfo,
  getDataContractId
} from 'utils/helpers'

import dynamic from 'next/dynamic'
import rd from 'react-dates/lib/css/_datepicker.css'
import search from '../../styles/search.scss'
import profile_responsive from '../../styles/profile-responsive.scss'
import psycho_profile from '../../styles/psycho-profile.scss'
import profile from '../../styles/profile.scss'

import { Column, Row } from 'components/v2/Grid'
import { ProfileData } from 'components/v2/PsychologistProfile'
import { Calendar, CalendarFilter } from 'components/v2/CalendarCartVariant'
import { CaretLeft, CaretRight } from 'phosphor-react'
import ConsultationDuration from 'components/v2/PsychologistProfile/ConsultationDuration'
import PatientType from 'components/v2/PsychologistProfile/PatientType'
import PsychoPrice from 'components/v2/PsychoPrice/PsychoPrice'
import Avatar from 'components/v2/PsychologistProfile/Avatar'
import { EvaluationsCarousel } from 'components/Psicologo/EvaluationsCarousel'
import { sendViewItemEvent } from 'components/shared/DataLayer'
import { UnavailablePsyMessage } from 'components/v2/PsychologistProfile/UnavailablePsyMessage'

const DynamicModal = dynamic(() => import('components/v2/ShoppingCart/ScheduleConsultationModal'), {
  ssr: false
})

const DynamicHeader = dynamic(
  () => import('components/v2/Header').then(mod => mod.HeaderRendering),
  { ssr: false }
)

const DynamicFooter = dynamic(
  () => import('components/shared/FooterStatic/FooterIntercomClickableHome'),
  { ssr: false }
)

const listSymptoms = symptoms => {
  return `${symptoms.map(symptom => symptom).join(', ')}`
}

const PsychologistProfilePage = ({ psycho, evaluations }) => {
  const router = useRouter()
  const { user } = useUser()
  const { timezone } = useContext(TimezoneContext)
  const [isSchedulingModalOpen, setIsSchedulingModalOpen] = useState(false)
  const { filters } = useSelector(state => ({
    filters: state.filters
  }))

  const clinicId = psycho.schedules.find(schedule => schedule.nickname === 'Consultório Virtual').id
  const [isModalOpen, setIsModalOpen] = useState(false)
  const currentSchedule = {
    coupon: '',
    psico_name: psycho.name,
    psico_id: psycho.id,
    is_teleconf: true,
    prof_address_slug: psycho.prof_address_slug,
    social_thumb: psycho.social_thumb,
    crp: psycho.prof_register,
    evaluations: psycho.rating ? psycho.rating.total : 0,
    slug: psycho.slug,
    type: psycho.type,
    totalComments: psycho.evaluations.length,
    timezone: timezone,
    timestamp_slot: psycho.distance ? psycho.distance.schedule_id : ''
  }

  const [startDate, setStartDate] = useState(moment().format('YYYYMMDD'))
  const [endDate, setEndDate] = useState(
    moment()
      .add(4, 'days')
      .format('YYYYMMDD')
  )
  const [selectedDate, setSelectedDate] = useState(null)
  const [isCalendarLoading, setIsCalendarLoading] = useState(false)

  const userContractId = user?.contract?.id || getDataContractId()?.integration?.contract?.id

  const metaTags = {
    title: `Faça terapia com ${psycho?.name} | Vittude`,
    description: `Agende agora uma consulta com ${
      psycho?.name
    } - que tem especialidade no tratamento de ${listSymptoms(psycho?.symptoms)}`,
    url: `${process.env.VITTUDE_OFFLINE_URL}${router.asPath}`,
    image: `${psycho?.social_thumb}`
  }

  useEffect(() => {
    getPaymentsInfo(timezone)
  }, [timezone])

  useEffect(() => {
    sendViewItemEvent(
      {
        item_name: psycho?.name,
        item_id: psycho?.id,
        price: psycho?.prices_teleconf?.max
      },
      psycho.prices_teleconf.max
    )
  }, [])

  const { data: schedules, error: psyScheduleError } = useQuery({
    queryKey: ['getSchedule', psycho.slug, startDate, endDate, clinicId, userContractId, timezone],
    queryFn: () =>
      getScheduleByProfessionalSlug(
        psycho.slug,
        startDate,
        endDate,
        clinicId,
        null,
        userContractId,
        'online',
        timezone
      ),
    select: data => {
      const schedules = getDataFromScheduleResponse(data)
      const appointments = formatAppointmentDates(schedules.dates, schedules.schedule)

      return {
        schedule: schedules.schedule,
        appointments,
        price: data.data.content.price,
        contract_price: data.data.content.contract_price
      }
    },
    staleTime: 1000 * 60 * 5 // 5 minutes
  })

  const isPsyInPartnership =
    psyScheduleError?.response?.data?.log_content?.error_code !== 'professional_not_in_partnership'

  const previousDay = moment(startDate)
    .subtract(1, 'day')
    .isBefore(moment(), 'day')

  const lastDay = moment(endDate).isSameOrAfter(
    moment()
      .add(3, 'M')
      .format('YYYYMMDD')
  )

  const handleNextWeek = () => {
    const { newStartDate, newEndDate } = calculateNewDates(startDate, 5)

    setStartDate(newStartDate)
    setEndDate(newEndDate)
  }

  const handlePreviousWeek = () => {
    const isFirstDay = moment(startDate)
      .subtract(5, 'days')
      .isBefore(moment(), 'day')

    const daysToSubtract = isFirstDay ? 0 : 5

    const { newStartDate, newEndDate } = calculateNewDates(startDate, -daysToSubtract)

    setStartDate(newStartDate)
    setEndDate(newEndDate)
  }

  const handleMultipleScheduling = () => {
    setIsSchedulingModalOpen(!isSchedulingModalOpen)
  }

  const contractPrice = schedules?.appointments[0]?.hours[0]?.contract_price

  useEffect(() => {
    if (filters?.startDate !== moment().format('YYYY-MM-DD')) {
      setStartDate(filters?.startDate)
      setEndDate(filters?.endDate)
    }
  }, [filters?.startDate, filters?.endDate])

  return (
    <Wrapper
      metaTags={metaTags}
      cssFile={[rd, search, profile_responsive, psycho_profile, profile]}
      coupon={router?.query?.coupon}
    >
      <DynamicHeader />
      <Column mt={navHeight()}>
        <section itemscope='' itemtype='http://schema.org/Physician'>
          <Row
            mx='auto'
            width='100%'
            display={['none', 'none', 'flex']}
            maxWidth={['100%', '100%', '1140px']}
          >
            {!!psycho?.video_url && (
              <Column width='100%' mt='10px' ml='8.3%' maxWidth='91.6%' px='24px'>
                <Row
                  className='embed-responsive embed-responsive-21by9'
                  box-shadow='0px 7px 16px 3px #c8c8c8'
                  background='#000'
                  width='100%'
                  mb='30px'
                >
                  <iframe
                    className='embed-responsive-item'
                    src={`https://www.youtube.com/embed/${getYouTubeVideoId(psycho.video_url)}`}
                    allow='autoplay; encrypted-media'
                    allowfullscreen
                  />
                </Row>
              </Column>
            )}
          </Row>
          <Row
            maxWidth='1140px'
            mx='auto'
            my='30px'
            width='100%'
            flexDirection={['column', 'column', 'row']}
            gridGap='16px'
          >
            <Column
              maxWidth={['100%', '100%', '16.666667%']}
              width='100%'
              px={['16px', '16px', '0px']}
              alignItems='center'
            >
              <Row width='130px' height='130px' mx='auto'>
                <Avatar psycho={psycho} isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
              </Row>
              <Row
                width='100%'
                justifyContent='center'
                mt='20px'
                flexDirection={['row', 'row', 'column']}
                gridGap='16px'
                alignItems='center'
                borderRadius={['8px', '8px', '0px']}
                display={['none', 'none', 'flex']}
              >
                {isPsyInPartnership ? (
                  <PsychoPrice
                    originalPrice={schedules?.price?.value}
                    contractPrice={schedules?.contract_price?.value}
                    showPaymentMethodMessage
                  />
                ) : (
                  <Row maxWidth='160px' width='100%'>
                    <UnavailablePsyMessage />
                  </Row>
                )}
                <ConsultationDuration />
                <PatientType psycho={psycho} />
              </Row>
            </Column>
            <Column width='100%' px='16px'>
              <ProfileData
                psycho={psycho}
                contractPrice={contractPrice}
                schedules={schedules}
                isPsyInPartnership={isPsyInPartnership}
              />
            </Column>
            {isPsyInPartnership && (
              <Column
                width='100%'
                mt='32px'
                maxWidth={['100%', '100%', '360px']}
                gridGap='16px'
                padding={['16px', '16px', '0px']}
              >
                <CalendarFilter
                  startDate={startDate}
                  handleDatesChange={date => {
                    setStartDate(moment(date).format('YYYYMMDD'))
                    setEndDate(
                      moment(date)
                        .add(4, 'days')
                        .format('YYYYMMDD')
                    )
                  }}
                />
                <Row width='100%' justifyContent='center'>
                  <CaretLeft
                    size={24}
                    color={previousDay ? '#E7EAF4' : '#FF7708'}
                    cursor={previousDay ? 'not-allowed' : 'pointer'}
                    onClick={() => !previousDay && handlePreviousWeek()}
                  />
                  <Calendar
                    id='calendar-id'
                    className='calendar-class'
                    currentSchedule={currentSchedule}
                    isSearch={true}
                    isPsychologist={true}
                    showWarnings={false}
                    isLoading={isCalendarLoading}
                    consultationAmount={1}
                    schedules={schedules}
                    clinicId={clinicId}
                    selectedDate={selectedDate}
                    setSelectedDate={value => setSelectedDate(value)}
                    setIsDisabledLoading={value => setIsCalendarLoading(value)}
                    showNotifications={true}
                    handleMultipleScheduling={handleMultipleScheduling}
                  />
                  <CaretRight
                    size={24}
                    color={lastDay ? '#E7EAF4' : '#FF7708'}
                    cursor={lastDay ? 'not-allowed' : 'pointer'}
                    onClick={() => !lastDay && handleNextWeek()}
                  />
                </Row>
              </Column>
            )}
          </Row>
        </section>
        <EvaluationsCarousel psychoEvaluations={evaluations} />
      </Column>
      <DynamicModal
        professional={psycho}
        isModalOpen={isSchedulingModalOpen}
        toggleModal={handleMultipleScheduling}
      />
      <DynamicFooter />
    </Wrapper>
  )
}

export const getServerSideProps = async ctx => {
  const dateNow = moment().format('YYYYMMDD')
  const timeNow = moment().format('HH:MM')
  const timezone = ctx.query.timezone || 'America/Sao_Paulo'

  try {
    const [{ data: psycho, status }, { data: paymentMethods }] = await Promise.all([
      getProfessionalInfoBySlug(ctx.query.slug),
      getPaymentMethodsInfo(dateNow, timeNow, timezone)
    ])

    if (status === 204) {
      return {
        redirect: {
          destination: '/psicologo-indisponivel'
        }
      }
    }

    const { data: evaluations } = await getProfessionalEvaluations(psycho.id, 1)

    return {
      props: {
        slug: ctx.query.slug,
        psycho: psycho,
        evaluations: evaluations?.content,
        paymentMethods: paymentMethods
      }
    }
  } catch (error) {
    if (error.response.data.code === 301) {
      ctx.res.writeHead(301, {
        Location: `http://${ctx.req.headers.host}/psicologo/` + error.response.data.location
      })
    } else {
      ctx.res.writeHead(302, {
        Location: `http://${ctx.req.headers.host}/404`
      })
    }
    ctx.res.end()
  }
}

export default PsychologistProfilePage
